import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MessageSquare,
  MoreHorizontal,
  Trash2,
  Pencil,
  SendHorizontal,
  Zap,
  ArrowLeft,
  Loader2,
  ExternalLink,
  Search,
  CalendarClock,
  Share2,
  Copy,
  Check,
  MapPin,
} from "lucide-react";
import { getBrowserId } from "@/lib/browserId";
import {
  createSession,
  deleteSession,
  getMessages,
  listSessions,
  renameSession,
  streamChat,
  shareSession,
  unshareSession,
} from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const SUGGESTIONS = [
  "What is an EPC certificate?",
  "How do I improve my EPC rating from D to C?",
  "How much does an EPC cost?",
  "What is MEES and does it apply to my rental?",
  "How do I find a local EPC provider?",
  "How do I train to become an EPC assessor?",
];

export default function Chat() {
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [userId] = useState(() => getBrowserId());
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(routeId || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingAssistant, setPendingAssistant] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null); // { slug, url, is_public }
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);

  // Load sessions
  useEffect(() => {
    listSessions(userId).then(setSessions).catch(() => {});
  }, [userId]);

  // Sync active id from URL
  useEffect(() => {
    setActiveId(routeId || null);
  }, [routeId]);

  // Prefill composer from ?prefill=... (e.g. from /check)
  useEffect(() => {
    const pf = searchParams.get("prefill");
    if (pf) {
      setInput(pf);
      searchParams.delete("prefill");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Load messages when session changes
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    getMessages(activeId).then(setMessages).catch(() => setMessages([]));
  }, [activeId]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pendingAssistant]);

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) || null,
    [sessions, activeId]
  );

  async function ensureSession(firstUserContent) {
    if (activeId) return activeId;
    const title = firstUserContent.slice(0, 60) || "New chat";
    const created = await createSession(userId, title);
    setSessions((s) => [created, ...s]);
    setActiveId(created.id);
    navigate(`/chat/${created.id}`, { replace: true });
    return created.id;
  }

  async function handleNewChat() {
    setActiveId(null);
    setMessages([]);
    navigate(`/chat`);
  }

  async function handleSend(text) {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setPendingAssistant("");

    try {
      const sid = await ensureSession(content);
      // Optimistic user message
      setMessages((m) => [
        ...m,
        { id: `tmp_${Date.now()}`, role: "user", content, session_id: sid },
      ]);

      let acc = "";
      await streamChat({
        session_id: sid,
        user_id: userId,
        content,
        onDelta: (chunk) => {
          acc += chunk;
          setPendingAssistant(acc);
        },
        onDone: () => {
          setMessages((m) => [
            ...m,
            {
              id: `a_${Date.now()}`,
              role: "assistant",
              content: acc,
              session_id: sid,
            },
          ]);
          setPendingAssistant("");
          // Refresh sessions to pick up updated title/updated_at
          listSessions(userId).then(setSessions).catch(() => {});
        },
        onError: (msg) => {
          toast.error("Chat error: " + msg);
        },
      });
    } catch (e) {
      toast.error(e.message || "Failed to send");
    } finally {
      setStreaming(false);
    }
  }

  async function handleRename(id) {
    const current = sessions.find((s) => s.id === id);
    const t = prompt("Rename chat", current?.title || "");
    if (!t || !t.trim()) return;
    try {
      const updated = await renameSession(id, t.trim());
      setSessions((s) => s.map((x) => (x.id === id ? updated : x)));
    } catch {
      toast.error("Rename failed");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this chat?")) return;
    try {
      await deleteSession(id);
      setSessions((s) => s.filter((x) => x.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
        navigate("/chat");
      }
    } catch {
      toast.error("Delete failed");
    }
  }

  async function openShare() {
    if (!activeId) {
      toast.error("Send a message first, then share.");
      return;
    }
    setShareOpen(true);
    setCopied(false);
    setShareLoading(true);
    try {
      const res = await shareSession(activeId);
      const url = `${window.location.origin}/s/${res.slug}`;
      setShareInfo({ slug: res.slug, url, is_public: true });
    } catch {
      toast.error("Could not create share link");
      setShareOpen(false);
    } finally {
      setShareLoading(false);
    }
  }

  async function handleCopy() {
    if (!shareInfo?.url) return;
    try {
      await navigator.clipboard.writeText(shareInfo.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  }

  async function handleStopSharing() {
    if (!activeId) return;
    try {
      await unshareSession(activeId);
      setShareInfo(null);
      setShareOpen(false);
      toast.success("Sharing disabled");
    } catch {
      toast.error("Could not disable sharing");
    }
  }

  return (
    <div className="h-screen w-screen flex bg-background text-foreground font-sans overflow-hidden" data-testid="chat-page">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-72 flex-col border-r border-border bg-neutral-50">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 flex-1" data-testid="back-home-link">
            <Logo className="h-8 w-8" />
            <span className="font-heading text-sm tracking-tight">chatepc.co.uk</span>
          </Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground" aria-label="Home">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
        <div className="p-3">
          <Button
            onClick={handleNewChat}
            data-testid="new-chat-btn"
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 justify-start"
          >
            <Plus className="h-4 w-4 mr-2" /> New chat
          </Button>
        </div>
        <div className="px-3 pb-2 text-[11px] uppercase tracking-widest text-muted-foreground">History</div>
        <ScrollArea className="flex-1 px-2 pb-4">
          {sessions.length === 0 && (
            <div className="px-2 py-6 text-sm text-muted-foreground">No chats yet.</div>
          )}
          <ul className="space-y-1">
            {sessions.map((s) => (
              <li
                key={s.id}
                data-testid={`session-row-${s.id}`}
                className={`group flex items-center gap-2 px-2 py-2 border border-transparent hover:border-border hover:bg-white cursor-pointer ${
                  activeId === s.id ? "bg-white border-border" : ""
                }`}
                onClick={() => navigate(`/chat/${s.id}`)}
              >
                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate text-sm">{s.title || "Untitled"}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"
                      data-testid={`session-menu-${s.id}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none">
                    <DropdownMenuItem onClick={() => handleRename(s.id)}>
                      <Pencil className="h-4 w-4 mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(s.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        </ScrollArea>
        <div className="p-3 border-t border-border text-[11px] text-muted-foreground">
          Chats saved to this browser only.
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="h-14 border-b border-border flex items-center px-4 gap-3">
          <Link to="/" className="md:hidden text-muted-foreground" data-testid="mobile-home-link">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-epc-a" />
            <div className="font-heading tracking-tight text-sm md:text-base truncate">
              {activeSession?.title || "New chat"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-muted-foreground hidden md:block">
              Powered by Claude Sonnet 5 · UK EPCs
            </div>
            {activeId && messages.length > 0 && (
              <Button
                onClick={openShare}
                data-testid="share-chat-btn"
                variant="outline"
                size="sm"
                className="rounded-none border-foreground/20"
              >
                <Share2 className="h-4 w-4 md:mr-1.5" />
                <span className="hidden md:inline">Share</span>
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="messages-scroll">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            {messages.length === 0 && !streaming && <EmptyState onPick={handleSend} />}
            <ul className="space-y-6">
              {messages.map((m) => (
                <MessageBubble key={m.id} role={m.role} content={m.content} />
              ))}
              {streaming && (
                <MessageBubble role="assistant" content={pendingAssistant} streaming />
              )}
            </ul>
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-border bg-background">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-end gap-2 border border-border bg-white p-2"
            >
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                placeholder="Ask anything about UK Energy Performance Certificates…"
                className="flex-1 resize-none bg-transparent outline-none px-2 py-2 text-sm md:text-base max-h-40"
              />
              <Button
                type="submit"
                data-testid="send-btn"
                disabled={streaming || !input.trim()}
                className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-10"
              >
                {streaming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SendHorizontal className="h-4 w-4" />
                )}
              </Button>
            </form>
            <div className="mt-2 text-[11px] text-muted-foreground text-center">
              ChatEPC provides general guidance on UK EPCs. Not legal advice.
            </div>
          </div>
        </div>
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Share2 className="h-4 w-4 text-epc-a" /> Share this chat
            </DialogTitle>
            <DialogDescription>
              Anyone with this link can read this conversation. They cannot post replies.
            </DialogDescription>
          </DialogHeader>

          {shareLoading ? (
            <div className="py-8 grid place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : shareInfo ? (
            <div className="space-y-4">
              <div className="flex items-stretch border border-border">
                <input
                  data-testid="share-url"
                  readOnly
                  value={shareInfo.url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-3 py-2 text-sm outline-none bg-white font-mono"
                />
                <button
                  onClick={handleCopy}
                  data-testid="copy-share-url"
                  className="px-3 border-l border-border bg-neutral-50 hover:bg-neutral-100 text-sm flex items-center gap-1"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-epc-a" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                The link stays active until you disable sharing here.
              </p>
            </div>
          ) : null}

          <DialogFooter className="mt-2 flex-row justify-between sm:justify-between gap-2">
            <Button
              variant="ghost"
              onClick={handleStopSharing}
              data-testid="stop-sharing-btn"
              className="rounded-none text-destructive hover:text-destructive"
              disabled={!shareInfo || shareLoading}
            >
              Stop sharing
            </Button>
            <Button
              onClick={() => setShareOpen(false)}
              className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onPick }) {
  const [pc, setPc] = useState("");
  function submit(e) {
    e.preventDefault();
    const q = pc.trim();
    if (!q) return;
    onPick(`Check the EPC for postcode ${q.toUpperCase()}`);
    setPc("");
  }
  return (
    <div className="animate-fade-up" data-testid="empty-state">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">Specialist assistant</div>
      <h1 className="mt-2 font-heading text-3xl md:text-4xl tracking-tight">
        What do you want to know about your <span className="bg-epc-a text-white px-2">EPC</span>?
      </h1>
      <p className="mt-3 text-muted-foreground">
        Pick a starter or type your own question. UK-focused, Claude Sonnet 5 under the hood.
      </p>
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            data-testid={`suggestion-${i}`}
            className="text-left border border-border p-4 hover:bg-neutral-50 transition-colors"
          >
            <div className="text-xs uppercase tracking-widest text-epc-a">Try</div>
            <div className="mt-1 font-heading text-base">{s}</div>
          </button>
        ))}
      </div>

      <form
        onSubmit={submit}
        className="mt-8 flex items-stretch max-w-xl border border-foreground/20 bg-white"
        data-testid="empty-check-form"
      >
        <div className="pl-3 flex items-center text-muted-foreground">
          <MapPin className="h-4 w-4" />
        </div>
        <input
          data-testid="empty-postcode"
          value={pc}
          onChange={(e) => setPc(e.target.value)}
          placeholder="Check any UK property — enter postcode (e.g. LS1 4DL)"
          className="flex-1 outline-none px-2 py-2 text-sm md:text-base bg-transparent"
        />
        <Button
          type="submit"
          data-testid="empty-check-btn"
          disabled={!pc.trim()}
          className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-11 px-5"
        >
          <Search className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Check EPC</span>
        </Button>
      </form>
      <p className="mt-2 text-xs text-muted-foreground">
        Live data from the UK government register (England &amp; Wales).
      </p>
    </div>
  );
}

function MessageBubble({ role, content, streaming = false }) {
  const isUser = role === "user";
  const showAssessorCta =
    !isUser &&
    !streaming &&
    /theepcman\.co\.uk|theepcregister\.co\.uk/i.test(content || "");
  return (
    <li
      className={`flex flex-col ${isUser ? "items-end" : "items-start"} gap-3`}
      data-testid={`msg-${role}`}
    >
      <div
        className={`max-w-[85%] px-4 py-3 leading-relaxed text-[15px] ${
          isUser
            ? "bg-epc-a text-white"
            : "bg-neutral-100 text-foreground border border-border"
        }`}
      >
        {isUser ? (
          <div className={`msg-body whitespace-pre-wrap ${streaming ? "caret" : ""}`}>
            {content}
          </div>
        ) : (
          <div className={`msg-body ${streaming ? "caret" : ""}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-epc-a underline underline-offset-2 hover:text-epc-a/80 break-words"
                  />
                ),
              }}
            >
              {content || ""}
            </ReactMarkdown>
          </div>
        )}
      </div>
      {showAssessorCta && <AssessorCtaCard />}
    </li>
  );
}

function AssessorCtaCard() {
  return (
    <div
      data-testid="assessor-cta-card"
      className="max-w-[85%] w-full border border-epc-a bg-white animate-fade-up"
    >
      <div className="flex items-center gap-2 bg-epc-a text-white px-4 py-2 text-xs uppercase tracking-widest font-medium">
        <Zap className="h-3.5 w-3.5" />
        Find an EPC assessor
      </div>
      <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
        <a
          href="https://theepcman.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-theepcman"
          className="group p-4 flex items-start gap-3 hover:bg-neutral-50"
        >
          <CalendarClock className="h-5 w-5 text-epc-a shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-heading text-base tracking-tight flex items-center gap-1">
              theepcman.co.uk
              <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              Book an EPC assessment with a provider covering your area — providing EPCs since 2011.
            </div>
          </div>
        </a>
        <a
          href="https://theepcregister.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-theepcregister"
          className="group p-4 flex items-start gap-3 hover:bg-neutral-50"
        >
          <Search className="h-5 w-5 text-epc-a shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-heading text-base tracking-tight flex items-center gap-1">
              theepcregister.co.uk
              <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              Search The EPC Register and find accredited assessors near you.
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}
